import * as React from 'react';
import { cn } from '../../utils';
import { ArrowUpRight, LucideIcon, TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface KpiCardDelta {
  value: string;
  direction: 'up' | 'down' | 'neutral';
  label?: string;
}

interface KpiCardProps extends React.HTMLAttributes<HTMLDivElement> {
  label: string;
  value: React.ReactNode;
  currency?: string;
  delta?: KpiCardDelta;
  subtext?: string;
  // Accepts either a Lucide component reference (icon={Wallet})
  // or an icon JSX element (icon={<Wallet className="size-4" />}).
  icon?: LucideIcon | React.ReactElement;
  iconClass?: string;
  iconBgClass?: string;
  // Legacy: rr-client-portal call-sites pass these.
  trend?: string;
  trendDir?: 'up' | 'down' | 'neutral';
  active?: boolean;
  danger?: boolean;
}

const deltaColorClass = { up: 'text-positive', down: 'text-negative', neutral: 'text-muted-foreground' };
const DeltaIcon = { up: TrendingUp, down: TrendingDown, neutral: Minus };

export function KpiCard({
  label, value, delta, subtext, icon, iconClass, iconBgClass, trend, trendDir, active = false, danger = false, className, ...props
}: KpiCardProps) {
  // Resolve the icon prop: component ref → render with sizing classes,
  // already-rendered element → use as-is. Also tolerate `null`/`undefined`.
  const iconNode = React.isValidElement(icon)
    ? icon
    : icon
      ? React.createElement(icon as LucideIcon, { className: cn('w-4 h-4', iconClass ?? 'text-primary') })
      : null;

  // Back-compat: synthesize a delta when only `trend`/`trendDir` were passed.
  const effectiveDelta = delta
    ?? (trend ? { value: trend, direction: trendDir ?? 'neutral' } as KpiCardDelta : undefined);
  const interactive = typeof props.onClick === 'function';

  return (
    <div
      className={cn(
        'rr-surface group flex min-h-[132px] flex-col justify-between p-5 text-card-foreground transition-all',
        interactive && 'cursor-pointer select-none hover:border-primary/35 hover:shadow-sm',
        active && 'border-primary/50 bg-primary/[0.03] ring-2 ring-primary/30',
        danger && 'border-destructive/30 bg-destructive/[0.025]',
        className,
      )}
      {...props}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2.5">
          {iconNode && (
            <div className={cn('grid size-9 shrink-0 place-items-center rounded-lg', danger ? 'bg-destructive/10' : (iconBgClass ?? 'bg-primary/10'))}>
              {iconNode}
            </div>
          )}
          <p className={cn('text-[12px] font-medium', danger ? 'text-destructive' : 'text-muted-foreground')}>{label}</p>
        </div>
        {interactive && <ArrowUpRight className="size-3.5 text-muted-foreground transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />}
      </div>
      <div className="mt-4">
        <p className="text-[28px] font-semibold leading-none tracking-[-0.04em] text-foreground tnum">{value}</p>
        {effectiveDelta && (
          <div className={cn('mt-2 flex items-center gap-1', deltaColorClass[effectiveDelta.direction])}>
            {React.createElement(DeltaIcon[effectiveDelta.direction], { size: 12 })}
            <span className="text-[11px] font-medium">{effectiveDelta.value}</span>
            {effectiveDelta.label && <span className="text-[11px] text-muted-foreground">{effectiveDelta.label}</span>}
          </div>
        )}
        {subtext && !effectiveDelta && <p className="text-[11px] text-muted-foreground mt-0.5">{subtext}</p>}
      </div>
    </div>
  );
}
